import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Autocomplete,
  TextField
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import UndoIcon from '@mui/icons-material/Undo';
import TextInput from 'src/components/UI/input/TextInput';
import SelectInput from 'src/components/UI/input/SelectInput';
import PartialDateInput from 'src/components/UI/input/PartialDateInput';
import AutocompleteInput from 'src/components/UI/input/AutocompleteInput';
import {
  partialDateFromBackend,
  partialDateToBackend
} from 'src/util/partialDate';
import axios from 'src/axios';
import { CONSTANTS, APICONSTANTS } from 'src/components/Constants';
import { CONSTANTS as QUESTIONNAIRE_CONSTANTS } from 'src/components/questionnaire/Constants';
import {
  resolveEthnicityOptions,
  readEthnicitySettings,
  allEthnicitiesAlphabetical
} from 'src/util/ethnicity';
import { useAutoTranslation } from 'src/hooks/useTranslate';

const FIELDS = [
  {
    key: 'genderIdentity',
    label: 'Gender',
    type: 'select',
    options: CONSTANTS.genderIdentityOptions
  },
  { key: 'firstName', label: 'First Name', type: 'text' },
  { key: 'lastName', label: 'Last Name', type: 'text' },
  {
    key: 'dob',
    label: 'Date of Birth',
    type: 'partialDate',
    dateFields: {
      date: 'dob',
      estimated: 'estimatedDob',
      precision: 'dobPrecision'
    }
  },
  {
    key: 'dod',
    label: 'Date of Death',
    type: 'partialDate',
    dateFields: {
      date: 'dod',
      estimated: 'estimatedDod',
      precision: 'dodPrecision'
    }
  },
  {
    key: 'ethnicity',
    label: 'Ethnicity',
    type: 'autocompleteMulti',
    options: allEthnicitiesAlphabetical
  },
  { key: 'mobile', label: 'Mobile', type: 'text' },
  { key: 'email', label: 'Email', type: 'text' },
  { key: 'hospitalNumber', label: 'Hospital Number', type: 'text' },
  { key: 'healthcareId', label: 'Healthcare ID', type: 'text' },
  {
    key: 'externalPatientIds',
    label: 'External Patient IDs',
    type: 'chipsMulti'
  },
  {
    key: 'fertility',
    label: 'Fertility',
    type: 'select',
    options: (QUESTIONNAIRE_CONSTANTS.fertilityMap || CONSTANTS.fertilityMap)
      .filter((o) => o.value !== null)
      .map((o) => ({ label: o.label, value: o.value }))
  }
];

const arraysEqual = (a, b) => {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
};

const isEmpty = (field, source) => {
  if (field.type === 'partialDate') return !source[field.dateFields.date];
  if (field.type === 'autocompleteMulti' || field.type === 'chipsMulti') {
    return !Array.isArray(source[field.key]) || source[field.key].length === 0;
  }
  return (
    source[field.key] === undefined ||
    source[field.key] === null ||
    source[field.key] === ''
  );
};

// An option's label is our vocabulary, translated; any other value is the
// record's own and shown as stored.
const formatScalar = (field, value, t) => {
  if (value === undefined || value === null || value === '') return '—';
  if (field.type === 'select') {
    const match = field.options?.find((o) => o.value === value);
    return match ? t(match.label) : String(value);
  }
  return String(value);
};

const copyFieldFromDeleted = (field, source, target) => {
  if (field.type === 'partialDate') {
    const f = field.dateFields;
    target[f.date] = source[f.date] ?? '';
    target[f.estimated] = source[f.estimated] ?? false;
    target[f.precision] = source[f.precision] ?? 'full';
  } else {
    target[field.key] =
      field.type === 'autocompleteMulti' || field.type === 'chipsMulti'
        ? Array.isArray(source[field.key])
          ? source[field.key]
          : []
        : source[field.key] ?? '';
  }
};

const formatPersonName = (source) => {
  const fn = (source?.firstName || '').trim();
  const ln = (source?.lastName || '').trim();
  const name = `${fn} ${ln}`.trim();
  return name || '—';
};

export default function PersonDetailsStep({
  retained,
  deleted,
  updates,
  onUpdate,
  autoFilledKeys,
  onAutoFilled
}) {
  const t = useAutoTranslation();

  const [ethnicityOptions, setEthnicityOptions] = useState(
    allEthnicitiesAlphabetical
  );

  useEffect(() => {
    axios
      .get(APICONSTANTS.getEthnicity)
      .then((res) =>
        setEthnicityOptions(
          resolveEthnicityOptions(readEthnicitySettings(res.data))
        )
      )
      .catch(() => {});
  }, []);

  const visibleFields = FIELDS.filter((field) => !isEmpty(field, deleted)).map(
    (field) =>
      field.key === 'ethnicity'
        ? { ...field, options: ethnicityOptions }
        : field
  );
  const originalRetained = retained;

  useEffect(() => {
    if (!deleted || !retained) return;
    if (autoFilledKeys.__ran) return;

    const next = { ...updates };
    const filled = {};
    visibleFields.forEach((field) => {
      if (isEmpty(field, deleted)) return;
      if (!isEmpty(field, retained)) return;
      copyFieldFromDeleted(field, deleted, next);
      filled[field.key] = true;
    });
    onAutoFilled({ ...filled, __ran: true });
    if (Object.keys(filled).length > 0) {
      onUpdate(next);
    }
  }, [deleted]);

  const clearAutoFilled = (key) => {
    if (!autoFilledKeys[key]) return;
    onAutoFilled((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleChange = (key, value) => {
    onUpdate({ ...updates, [key]: value });
    clearAutoFilled(key);
  };

  const handlePartialDateChange = (field, partial) => {
    const patch = partialDateToBackend(partial, field.dateFields);
    onUpdate({ ...updates, ...patch });
    clearAutoFilled(field.key);
  };

  const handleRevertField = (field) => {
    const next = { ...updates };
    copyFieldFromDeleted(field, deleted, next);
    onUpdate(next);
    onAutoFilled((prev) => ({ ...prev, [field.key]: true }));
  };

  const renderInput = (field, source, disabled) => {
    const noop = () => {};
    switch (field.type) {
      case 'text':
        return (
          <TextInput
            name={field.key}
            value={source[field.key] ?? ''}
            onChange={
              disabled ? noop : (e) => handleChange(field.key, e.target.value)
            }
            disabled={disabled}
          />
        );

      case 'select':
        return (
          <SelectInput
            fullWidth
            variant="outlined"
            label=""
            name={field.key}
            options={field.options}
            value={source[field.key] ?? ''}
            onChange={
              disabled ? noop : (e) => handleChange(field.key, e.target.value)
            }
            disabled={disabled}
          />
        );

      case 'partialDate': {
        const f = field.dateFields;
        return (
          <PartialDateInput
            label=""
            value={partialDateFromBackend({
              iso: source[f.date],
              estimatedFlag: source[f.estimated],
              precisionField: source[f.precision]
            })}
            onChange={
              disabled
                ? noop
                : (partial) => handlePartialDateChange(field, partial)
            }
            disableFuture
            disabled={disabled}
          />
        );
      }

      case 'autocompleteMulti':
        return (
          <AutocompleteInput
            label=""
            multiple
            options={field.options}
            getOptionLabel={(option) => option}
            value={source[field.key] || []}
            onChange={disabled ? noop : (val) => handleChange(field.key, val)}
            disabled={disabled}
          />
        );

      case 'chipsMulti':
        return (
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={source[field.key] || []}
            onChange={
              disabled
                ? noop
                : (_, newValue) => handleChange(field.key, newValue)
            }
            disabled={disabled}
            renderValue={(value, getItemProps) =>
              value.map((option, index) => (
                <Chip
                  key={option}
                  label={option}
                  size="small"
                  {...getItemProps({ index })}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={disabled ? '' : t('Type and press Enter')}
                size="small"
              />
            )}
          />
        );

      default:
        return null;
    }
  };

  const formatOriginal = (field) => {
    if (field.type === 'partialDate') {
      const v = originalRetained[field.dateFields.date];
      return v ? String(v) : t('(empty)');
    }
    if (field.type === 'autocompleteMulti' || field.type === 'chipsMulti') {
      const list = originalRetained[field.key];
      if (Array.isArray(list) && list.length > 0) return list.join(', ');
      return t('(empty)');
    }
    const v = originalRetained[field.key];
    if (v === undefined || v === null || v === '') return t('(empty)');
    return formatScalar(field, v, t);
  };

  const hasDivergedFromOriginal = (field) => {
    if (field.type === 'partialDate') {
      const f = field.dateFields;
      return (
        updates[f.date] !== originalRetained[f.date] ||
        !!updates[f.estimated] !== !!originalRetained[f.estimated] ||
        (updates[f.precision] || 'full') !==
          (originalRetained[f.precision] || 'full')
      );
    }
    if (field.type === 'autocompleteMulti' || field.type === 'chipsMulti') {
      return !arraysEqual(
        updates[field.key] || [],
        originalRetained[field.key] || []
      );
    }
    return updates[field.key] !== originalRetained[field.key];
  };

  if (visibleFields.length === 0) {
    return (
      <Box
        sx={{
          py: 4,
          textAlign: 'center',
          color: 'text.secondary'
        }}
      >
        <Typography variant="body2">
          {t(
            'The deleted person has no personal-detail fields populated. Continue to the next step.'
          )}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          mb: 2
        }}
      >
        {t(
          'Fields auto-filled from the deleted person are highlighted in green. Edit them to override. Use the'
        )}{' '}
        <UndoIcon sx={{ fontSize: 13, verticalAlign: 'middle' }} />{' '}
        {t('revert button to restore the auto-filled value.')}
      </Typography>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          backgroundColor: 'background.paper',
          pb: 1.5,
          mb: 1,
          borderBottom: 1,
          borderColor: 'divider'
        }}
      >
        <Grid
          container
          spacing={2}
          sx={{
            alignItems: 'center'
          }}
        >
          <Grid size={6}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
                justifyContent: 'center'
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: 'success.dark'
                }}
              >
                {formatPersonName(originalRetained)}
              </Typography>
              <Chip
                icon={<CheckCircleOutlineIcon />}
                label={t('Retained (editable)')}
                color="success"
                size="small"
                variant="outlined"
              />
            </Box>
          </Grid>
          <Grid size={6}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
                justifyContent: 'center'
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: 'error.dark'
                }}
              >
                {formatPersonName(deleted)}
              </Typography>
              <Chip
                icon={<DeleteOutlineIcon />}
                label={t('Deleted (reference only)')}
                color="error"
                size="small"
                variant="outlined"
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
      {visibleFields.map((field) => {
        const autoFilled = !!autoFilledKeys[field.key];

        return (
          <Box
            key={field.key}
            sx={{
              mb: 2
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                mb: 0.5,
                pl: 0.5
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 600
                }}
              >
                {t(field.label)}
              </Typography>

              {isEmpty(field, originalRetained) && !autoFilled && (
                <Tooltip
                  placement="top"
                  title={t('Revert to auto-filled value')}
                >
                  <IconButton
                    size="small"
                    sx={{ p: 0.25 }}
                    onClick={() => handleRevertField(field)}
                  >
                    <UndoIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Grid container spacing={2}>
              <Grid size={6}>
                <Box
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: autoFilled
                        ? 'rgba(46, 125, 50, 0.08)'
                        : 'transparent'
                    },
                    '& .MuiOutlinedInput-root.Mui-focused fieldset': {
                      borderColor: 'success.main'
                    },
                    ...(autoFilled && {
                      '& .MuiOutlinedInput-root fieldset': {
                        borderColor: 'success.main'
                      }
                    })
                  }}
                >
                  {renderInput(field, updates, false)}
                </Box>
              </Grid>
              <Grid size={6}>{renderInput(field, deleted, true)}</Grid>
            </Grid>
          </Box>
        );
      })}
    </Box>
  );
}
